import { Callout } from './components/callout.tsx'

export const meta = () => [
	{ title: 'Performance Monitoring · xcelera docs' },
	{
		name: 'description',
		content:
			'Best practices for choosing audit frequency, interpreting scores and metrics, and tracking regressions.',
	},
]

# Performance Monitoring

Best practices for getting the most out of xcelera's performance auditing.

## Choosing an audit frequency

The right frequency depends on how often your site changes and how quickly you need to catch regressions:

- **Daily** — good default for most production pages. Catches regressions within a day.
- **Weekly** — suitable for low-traffic or infrequently updated pages.
- **Hourly** — best for high-traffic pages on active projects where you deploy frequently. Uses more credits.

<Callout type="tip">
	Start with daily audits and increase frequency for pages that matter most. You
	can always adjust later.
</Callout>

## Understanding scores and metrics

Lighthouse produces scores (0–100) across four categories, but the individual metrics are often more actionable:

- **LCP (Largest Contentful Paint)** — how long until the main content is visible. Target: under 2.5 seconds.
- **TBT (Total Blocking Time)** — how long the main thread is blocked. Target: under 200ms.
- **CLS (Cumulative Layout Shift)** — how much the page layout shifts during loading. Target: under 0.1.

These three metrics are the core web vitals that most directly impact user experience.

## Tracking regressions

xcelera automatically detects when a page's performance score drops by 5 or more points. To make the most of regression detection:

1. **Set up notifications** — configure [notification levels](/docs/notifications) to alert you on score regressions
2. **Review the audit detail** — click into a regressed audit to see which specific metrics changed
3. **Check the git context** — if the audit was triggered from CI, the commit context helps identify what caused the regression

## Reducing score variance

Lighthouse scores have natural variance between runs. xcelera reduces this by running Lighthouse multiple times per audit and selecting a representative result. To further reduce noise:

- Audit the same URL consistently (avoid redirects or A/B tests that change content between runs)
- Ensure the page is reliably reachable — intermittent network issues inflate variance
- Look at trends across multiple audits rather than reacting to single data points

## Setting up alerts

Combine [notifications](/docs/notifications) with [webhooks](/docs/webhooks) to build an alerting pipeline:

- Use **Warning** level notifications to catch failures and score regressions
- Route webhooks to Slack for team visibility
- Use **Critical** level for PagerDuty or on-call systems
